/**
 * /truth-table — страница: выражение → таблица истинности и всё, что из неё следует.
 *
 * Принципы:
 *   • считать на лету — ни одной кнопки «Построить»: набрал — таблица уже рядом;
 *   • показывать, КАК прочитана запись («прочитано как: (A ∧ B) → ¬C») и где
 *     знак читался двояко — с переключателем прямо в заметке;
 *   • таблица — как в тетради: столбцы действий в школьном порядке с номером;
 *   • обратный путь: клик по клетке карты Карно или столбца F меняет функцию,
 *     и формулы пересчитываются («нарисуй функцию — получи формулу»);
 *   • всё, что тяжелее таблицы (минимизация, законы, схема), считается, когда
 *     человек открыл вкладку.
 *
 * ⚠️ Слова интерфейса — только из CFG.i18n (ключи перечислены в блейде).
 * ⚠️ Текст выражения в телеметрию не уходит — только класс, длина, число переменных.
 */

import { parseInput, LogicParseError } from './parse.js';
import { buildModel, verdict, differences, vectorString, rowBits, dummyVars, DC } from './logic.js';
import { toDoc, docHtml, docText, docLatex, fmtHtml, fmtText, varText, varHtml, dnfAst, cnfAst, juxtOk, opSymbol } from './format.js';
import { sdnf, sknf, minimize, literalCount, cubeString, cubeMinterms, covers, QMC_MAX_VARS } from './forms.js';
import { simplifyByLaws, literalsIn, LAW_FORMULAS } from './laws.js';
import { zhegalkinTriangle, zhegalkinTerms, postClasses } from './algebra.js';
import { kmapLayout, kmapSvg } from './kmap.js';
import { solveEge, randomEgeTask } from './ege.js';
import { EGE_SAMPLES } from './ege-samples.js';
import { circuitSvg, gateCount } from './circuit.js';
import { toTsv, toCsv, toMarkdown, toLatex, pngScale, tableBlocks } from './export.js';
import { fill, fillHtml } from './i18n.js';
import { notationFor, schoolSigns, inSchoolSigns, rowOrder } from './notation.js';

const CFG = window.__TT_CFG || {};
const I18N = CFG.i18n || {};
const SLUG = 'truth-table';
const LOCALE = String(window.__LOCALE || document.documentElement.lang || 'en').slice(0, 2);
const $ = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* ── слова ───────────────────────────────────────────────────────────────── */
/** Строка перевода как есть. Пропущенный ключ виден как ключ — так его находит сторож. */
const phrase = (k) => (I18N[k] != null ? String(I18N[k]) : k);
/** Перевод ТЕКСТОМ (aria-label, подсказка, тост, буфер) с подстановкой :имя. */
const t = (k, p) => fill(phrase(k), p);
/**
 * Перевод в HTML: слова экранируются, а ЗАПИСИ (наборы, номера, знаки, формулы)
 * встают в изоляцию слева направо — в арабском абзаце иначе «(0, 1, 1)» читается
 * наоборот, а «(» из сообщения об ошибке зеркалится в «)».
 */
const th = (k, p) => fillHtml(phrase(k), p);
/** Запись (не слова): значение подстановки, которое изолируется. */
const rec = (s) => ({ ltr: String(s) });
/**
 * «F = 1» — одна запись целиком: перевод держит на её месте :feq. Половина записи в
 * переводе и половина в подстановке давали в арабском абзаце «0 = F», а на телефоне
 * строка рвалась внутри записи — поэтому изоляция и запрет переноса.
 */
const fEq = (b) => ({ ltr: 'F = ' + b, nowrap: true });

/* ── запись страны ───────────────────────────────────────────────────────── */
// Умолчания поведения (чтение цепочки →, обозначения вентилей, разделитель CSV,
// экзамен) — одна таблица в notation.js; знаки школьной записи — из перевода.
const NT = notationFor(LOCALE, typeof navigator !== 'undefined' ? navigator.languages : []);
const SIGNS = schoolSigns(phrase);
const WORDS = {
    not: t('word_not'), and: t('word_and'), or: t('word_or'), xor: t('word_xor'),
    imp: t('word_imp'), rimp: SIGNS.rimp, eq: t('word_eq'), nand: t('word_nand'), nor: t('word_nor'),
};

const PREF_KEYS = {
    // order: '' — человек не выбирал; тогда порядок строк — умолчание страны (rowOrder в notation.js)
    notation: 'math', values: '01', order: '', inter: true, varOrder: 'alpha',
    split: 'auto', impAssoc: NT.imp, gate: NT.gate, kmTarget: 1, circSrc: 'expr',
};
// Порядок наборов на кнопке — запись, одна для всех языков (кнопка стоит в dir=ltr).
const ROW_ORDER = { asc: '000 → 111', desc: '111 → 000' };
/** Порядок строк сейчас: выбор человека, иначе умолчание страны для показанных значений. */
const curOrder = () => rowOrder(NT, state.prefs.values, state.prefs.order);
const PALETTE = ['#e5484d', '#2f9e66', '#3e63dd', '#e8740c', '#8e4ec6', '#0e9aa7', '#d6409f', '#a07e3c'];
const ROW_PAGE = 256;
const TRAINER_MAX = 32;

const state = {
    mode: 'expr',
    tab: 'table',
    src: '',
    parsed: null,
    model: null,
    error: null,
    active: 0,
    filter: 'all',
    shown: ROW_PAGE,
    fromExpr: null,
    trainer: null,
    ege: { rows: [], sample: -1 },
    prefs: Object.assign({}, PREF_KEYS),
    cache: {},
};

/* ── настройки ───────────────────────────────────────────────────────────── */
function loadPrefs() {
    const ts = window.__toolSettings;
    if (!ts || typeof ts.load !== 'function') return;
    for (const k of Object.keys(PREF_KEYS)) {
        const v = ts.load(SLUG, k, PREF_KEYS[k]);
        if (v != null && typeof v === typeof PREF_KEYS[k]) state.prefs[k] = v;
    }
}
function savePref(k, v) {
    state.prefs[k] = v;
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) { /* хранилище недоступно — работаем без памяти */ }
}

/* ── телеметрия ──────────────────────────────────────────────────────────────
   По ней чинят то, чего мы не видим:
     stage()        шаги воронки: input → (complete ставит __aeToolComplete);
     ae()           действие человека: ключевые (режим, пример, вкладка, выдача, тренажёр, задача о столбцах) —
                    в воронку (__aeFunnel), остальные — feature_use с именем в поле f;
     resultShown()  таблица построена — tool_complete, ОДИН раз на результат: режим, число переменных и
                    функций, вердикт, длина записи — без самой формулы;
     refused()      отказ разбора — tool_diagnostic с кодом и отвергнутой записью до 60 знаков: главный
                    прибор «чего люди хотели» (формула — не личные данные).
   Таблица строится на лету, поэтому результат и отказ ждут, пока человек перестанет набирать (SETTLE_MS):
   «A ∧» на пути к «A ∧ B» событием не становится. */
const SETTLE_MS = 1200;
const staged = {};
function stage(name, meta) {
    if (staged[name]) return;
    staged[name] = 1;
    try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) { /* телеметрия не мешает работе */ }
}
const FUNNEL = {
    mode: 1, sample: 1, start_tile: 1, tab: 1, share: 1, pdf: 1, print: 1, export: 1, copy_table: 1, history: 1,
    trainer_start: 1, trainer_check: 1, trainer_stop: 1, ege_sample: 1, ege_new: 1, ege_reveal: 1, compare_open: 1,
    notation: 1, values: 1, order: 1,
};
const ae = (action, p) => {
    try {
        if (FUNNEL[action] && window.__aeFunnel) window.__aeFunnel(action, p || {});
        else if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, f: action, action }, p || {}));
    } catch (_) { /* телеметрия не мешает работе */ }
};
let doneTimer = 0;
let doneSig = '';
let badTimer = 0;
let badSig = '';
/**
 * Результат показан по действию человека.
 * @param {object} payload  что построили — без текста человека
 * @param {string} sig      подпись результата (с самой записью; никуда не уходит): тот же результат, показанный
 *                          снова (другая вкладка, запись, порядок строк), вторым событием не становится
 * @param {Function} [then] что сделать, когда результат засчитан (недавние выражения)
 */
function resultShown(payload, sig, then) {
    stage('input');
    clearTimeout(badTimer);
    clearTimeout(doneTimer);
    doneTimer = setTimeout(() => {
        if (sig === doneSig) return;
        doneSig = sig;
        try { if (window.__aeToolComplete) window.__aeToolComplete(payload); } catch (_) { /* телеметрия не мешает работе */ }
        if (then) { try { then(); } catch (_) { /* история не мешает работе */ } }
    }, SETTLE_MS);
}
/** Отказ: код причины латиницей и отвергнутая запись (до 60 знаков). */
function refused(issue, input, extra) {
    stage('input');
    clearTimeout(doneTimer);
    clearTimeout(badTimer);
    const sig = issue + '|' + input;
    badTimer = setTimeout(() => {
        if (sig === badSig) return;
        badSig = sig;
        try {
            if (window.__aeDiagnostic) window.__aeDiagnostic(Object.assign({ issue, input: String(input == null ? '' : input).slice(0, 60) }, extra || {}));
        } catch (_) { /* телеметрия не мешает работе */ }
    }, SETTLE_MS);
}
/**
 * Страховка отрисовки: ошибка в коде вкладки не оставляет прежнюю таблицу под новой формулой —
 * человек читает фразу, а ярлык места и текст ошибки уходят в телеметрию.
 */
function guarded(label, fn) {
    try { return fn(); } catch (e) {
        try { if (window.__handleError) window.__handleError(e, 'tt_' + label, { say: t('err_internal') }); } catch (_) { /* телеметрия не мешает работе */ }
        try { console.error('[truth-table] ' + label, e); } catch (_) { /* консоль */ }
        return undefined;
    }
}
/** Готов ли сборщик PDF: скрипт отчёта мог не доехать — кнопка тогда не молчит. */
function pdfReady() {
    if (window.__generatePDF) return true;
    try { if (window.__toast) window.__toast(t('pdf_error'), 'error'); } catch (_) { /* тост не мешает работе */ }
    try { if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'pdf_engine_missing' }); } catch (_) { /* телеметрия не мешает работе */ }
    return false;
}

/* ── запись формул ───────────────────────────────────────────────────────── */
const fo = (extra) => Object.assign({
    notation: state.prefs.notation,
    words: WORDS,
    sym: SIGNS,
    juxt: state.prefs.notation === 'eng' && !!state.model && juxtOk(state.model.vars),
}, extra || {});
const fHtml = (ast, extra) => fmtHtml(ast, fo(extra));
const fText = (ast, extra) => fmtText(ast, fo(extra));
/**
 * Формула-пример ВНУТРИ фразы («…читают справа налево: A → (B → C)»): её собирает
 * код — в записи, выбранной на странице, и знаками школы страны, — а перевод
 * держит на её месте плейсхолдер. Буквы примера однобуквенные, поэтому в
 * инженерной записи они пишутся слитно.
 */
function sampleFormula(src) {
    const ast = parseInput(src, { locale: 'en' })[0].ast;
    const o = { notation: state.prefs.notation, words: WORDS, sym: SIGNS, juxt: state.prefs.notation === 'eng' };
    return { html: fmtHtml(ast, o), txt: fmtText(ast, o) };
}
const valLabels = () => {
    const v = state.prefs.values;
    if (v === 'word') return [t('val_false'), t('val_true')];
    if (v === 'tf') return ['F', 'T'];
    return ['0', '1'];
};
const cellText = (v) => (v === DC ? '–' : valLabels()[v]);
const fnLabel = (k, many) => (many ? 'F' + '₀₁₂₃₄₅₆₇₈₉'[k + 1 > 9 ? 9 : k + 1] : 'F');
const vText = (i) => varText(state.model.vars[i]);

/* ── разбор ──────────────────────────────────────────────────────────────── */
function currentSource() {
    if (state.mode === 'vector') {
        const names = $('ttVecNames').value.split(/[\s,;]+/).filter(Boolean);
        const vec = $('ttVector').value.replace(/[^01\-]/g, '');
        if (!vec) return '';
        return names.length ? 'F(' + names.join(', ') + ') = ' + vec : vec;
    }
    return $('ttInput').value;
}

function rebuild(fromInput) {
    const src = currentSource();
    state.src = src;
    state.cache = {};
    state.shown = ROW_PAGE;
    state.trainer = null;
    state.error = null;
    if (!src.trim()) {
        state.parsed = null;
        state.model = null;
        render();
        return;
    }
    try {
        const parsed = parseInput(src, { locale: LOCALE, impAssoc: state.prefs.impAssoc, split: state.prefs.split });
        const model = buildModel(parsed, { order: state.prefs.varOrder });
        state.parsed = parsed;
        state.model = model;
        if (state.active >= model.fns.length) state.active = 0;
    } catch (e) {
        state.parsed = null;
        state.model = null;
        if (e instanceof LogicParseError) state.error = e;
        else {
            state.error = new LogicParseError('internal', 0);
            // Не ошибка записи, а наша: человек читает фразу под полем, причина уходит в телеметрию.
            try { if (window.__handleError) window.__handleError(e, 'tt_build', { say: t('err_internal') }); } catch (_) { /* телеметрия не мешает работе */ }
        }
    }
    state.byUser = !!fromInput;
    guarded('render', render);
    if (fromInput && state.model && state.mode !== 'ege') {
        const m = state.model;
        resultShown({
            mode: state.mode, n: m.n, fns: m.fns.length, kind: verdict(activeCol()).kind, len: src.trim().length,
            dc: m.fns.some((f) => f.kind === 'vector' && f.vector.indexOf('-') >= 0) ? 1 : 0,
        }, state.mode + '|' + src.trim() + '|' + state.prefs.impAssoc + '|' + state.prefs.split, state.mode === 'expr' ? () => pushRecent(src.trim()) : null);
    } else if (fromInput && state.error && state.error.code !== 'empty') {
        const info = state.error.info || {};
        refused('parse_' + state.error.code, src, { mode: state.mode, at: state.error.at, ch: info.ch == null ? undefined : String(info.ch).slice(0, 8) });
    }
    saveHash();
}

const activeFn = () => state.model.fns[state.active] || state.model.fns[0];
const activeCol = () => activeFn().col;

/* ── общая отрисовка ─────────────────────────────────────────────────────── */
function render() {
    renderError();
    renderReading();
    renderVerdict();
    const has = !!state.model;
    $('ttEmpty').hidden = has || state.mode === 'ege';
    $('ttStage').hidden = !has || state.mode === 'ege';
    $('ttResult').hidden = !has;
    $('ttActions').hidden = state.mode === 'ege';
    if (state.mode === 'ege' || !has) wideNeed();
    if (state.mode === 'ege') { renderEge(); return; }
    if (!has) return;
    $('ttPrintHead').innerHTML = readingHtml() + $('ttVerdict').innerHTML;
    renderFnPicker();
    renderTab();
}

function renderError() {
    const box = $('ttError');
    const e = state.error;
    const input = state.mode === 'vector' ? $('ttVector') : $('ttInput');
    if (!e || e.code === 'empty') {
        box.hidden = true;
        input.removeAttribute('aria-invalid');
        return;
    }
    input.setAttribute('aria-invalid', 'true');
    const info = e.info || {};
    // Знак из записи человека («(», «<=») — запись: в арабской фразе скобка без
    // изоляции зеркалится, и сообщение называет не ту скобку.
    const shown = {};
    for (const name of Object.keys(info)) shown[name] = typeof info[name] === 'number' ? info[name] : rec(info[name]);
    let msg = th('err_' + e.code, shown);
    let snippet = '';
    if (state.mode !== 'vector' && typeof e.at === 'number' && e.code !== 'too_many_vars') {
        msg += ' ' + th('err_at', { n: e.at + 1 });
        const s = state.src;
        const len = Math.max(1, (info.len || String(info.ch || '').length || 1));
        snippet = '<code class="tt-snip" dir="ltr">' + esc(s.slice(0, e.at)) + '<mark>' + (esc(s.slice(e.at, e.at + len)) || '&nbsp;') + '</mark>' + esc(s.slice(e.at + len)) + '</code>';
    }
    box.innerHTML = '<span>' + msg + '</span>' + snippet;
    box.hidden = false;
}

/* ── «прочитано как» и заметки ───────────────────────────────────────────── */
function renderReading() {
    const box = $('ttReading');
    const notes = $('ttNotes');
    if (!state.model || state.mode === 'ege') {
        box.hidden = true;
        notes.hidden = true;
        if (state.mode === 'ege' && state.model) { box.hidden = false; box.innerHTML = readingHtml(); }
        return;
    }
    box.innerHTML = readingHtml();
    box.hidden = false;
    const list = [];
    const marks = {};
    let impChain = false;
    let split = [];
    let cands = [];
    let implicit = 0;
    for (const p of state.parsed) {
        if (p.kind !== 'expr') continue;
        Object.assign(marks, p.notes.marks);
        impChain = impChain || p.notes.impChain;
        if (p.notes.split) split = split.concat(p.notes.split);
        if (p.notes.splitCandidates) cands = cands.concat(p.notes.splitCandidates);
        implicit += p.notes.implicitAnd || 0;
    }
    if (impChain) {
        // Заметка называет выбранное чтение и показывает ДРУГОЕ — формулой, которую
        // собирает код (в переводе на её месте :f), и кнопкой переключения.
        const left = state.prefs.impAssoc === 'left';
        const note = left ? th('note_imp_left', { f: sampleFormula('A → (B → C)') }) : th('note_imp_right', { f: sampleFormula('(A → B) → C') });
        list.push(note + ' <button type="button" class="tt-link" data-tt-assoc="' + (left ? 'right' : 'left') + '">'
            + esc(left ? t('note_imp_switch_right') : t('note_imp_switch_left')) + '</button>');
    }
    if (split.length) {
        list.push(th('note_split', { list: rec(uniq(split).join(', ')) }) + ' <button type="button" class="tt-link" data-tt-split="off">' + esc(t('note_split_off')) + '</button>');
    } else if (cands.length) {
        list.push(th('note_split_cand', { list: rec(uniq(cands).join(', ')) }) + ' <button type="button" class="tt-link" data-tt-split="on">' + esc(t('note_split_on')) + '</button>');
    }
    // Знаки в этих заметках — записи: «<=» и «!=» в арабском абзаце без изоляции
    // выворачиваются и зеркалятся («<=» читается как «=>»).
    if (marks.tilde_eq) list.push(th('note_tilde', { sign: rec('~'), eq: rec(SIGNS.eq) }));
    if (marks.caret) list.push(th('note_caret', { sign: rec('^'), xor: rec(SIGNS.xor), and: rec(SIGNS.and), amp: rec(SIGNS.and === '&' ? '∧' : '&') }));
    if (marks.py_imp) list.push(th('note_py_imp', { le: rec('<='), ge: rec('>='), ex: rec('x <= y') }));
    if (marks.neq) list.push(th('note_neq', { sign: rec('!='), xor: rec(SIGNS.xor) }));
    if (marks.single_eq) list.push(th('note_single_eq', { sign: rec('='), eq: rec(SIGNS.eq) }));
    if (implicit && !split.length) list.push(th('note_implicit', { a: rec('AB'), b: rec('(A+B)(C+D)') }));
    if (state.model.fns.some((f) => f.kind === 'vector' && f.vector.indexOf('-') >= 0)) list.push(th('note_dc'));
    notes.innerHTML = list.map((s) => '<li>' + s + '</li>').join('');
    notes.hidden = !list.length;
}
const uniq = (a) => [...new Set(a)];

function readingHtml() {
    const many = state.model.fns.length > 1;
    return '<span class="tt-read-l">' + esc(t('read_as')) + '</span>' + state.model.fns.map((f, k) => {
        const name = f.name && !many ? f.name : fnLabel(k, many);
        const args = state.model.vars.map(varHtml).join(', ');
        const body = f.kind === 'vector'
            ? '<span class="tt-vec">' + esc(f.vector) + '</span>'
            : fHtml(f.ast, { full: true });
        return '<div class="tt-read-f" dir="ltr"><b>' + esc(varText(name)) + '(' + args + ')</b> = ' + body + '</div>';
    }).join('');
}

/* ── вердикт ─────────────────────────────────────────────────────────────── */
function renderVerdict() {
    const box = $('ttVerdict');
    if (!state.model || state.mode === 'ege') { box.hidden = true; return; }
    const m = state.model;
    const parts = [];
    if (m.fns.length > 1) {
        const base = m.fns[0].col;
        const diffs = m.fns.slice(1).map((f) => differences(base, f.col));
        const all = diffs.every((d) => !d.length);
        if (all) {
            parts.push('<p class="tt-v-main good">' + th('cmp_equal', { total: m.N }) + '</p>');
        } else {
            const bad = diffs.findIndex((d) => d.length);
            const r = diffs[bad][0];
            // «A = 0, B = 1, …» переносится только после запятой, не внутри «x = 0»
            const atParts = m.vars.map((v, i) => varText(v) + ' = ' + cellText(rowBits(r, m.n)[i]));
            const at = { html: atParts.map((x) => '<span class="tt-nw">' + esc(x) + '</span>').join(', '), txt: atParts.join(', ') };
            parts.push('<p class="tt-v-main bad">' + th('cmp_differ', { count: diffs[bad].length, total: m.N, a: rec(fnLabel(0, true)), b: rec(fnLabel(bad + 1, true)) }) + '</p>');
            // «F₁ = 1» и «A = 0, B = 1» — записи: каждая встаёт в фразу целиком.
            parts.push('<p class="tt-v-sub">' + th('cmp_example', {
                at,
                va: { ltr: fnLabel(0, true) + ' = ' + cellText(base[r]), nowrap: true },
                vb: { ltr: fnLabel(bad + 1, true) + ' = ' + cellText(m.fns[bad + 1].col[r]), nowrap: true },
            }) + ' <button type="button" class="tt-link" data-tt-filter="diff">' + esc(t('cmp_show')) + '</button></p>');
        }
    }
    const v = verdict(activeCol());
    const cls = v.kind === 'tautology' ? 'good' : v.kind === 'contradiction' ? 'warn' : '';
    const text = v.kind === 'tautology' ? th('verdict_taut', { feq: fEq(1), total: v.total })
        : v.kind === 'contradiction' ? th('verdict_contra', { feq: fEq(0), total: v.total })
            : th('verdict_sat', { feq: fEq(1), ones: v.ones, total: v.total });
    // При сравнении вердикт подписан своей функцией: «F₂: …» — подпись и двоеточие
    // ставит перевод (fr « : »), а не код.
    const line = m.fns.length > 1 ? th('of_fn', { fn: rec(fnLabel(state.active, true)), text: { phrase: text } }) : text;
    parts.push('<p class="tt-v-' + (m.fns.length > 1 ? 'sub' : 'main') + ' ' + cls + '">' + line + '</p>');
    if (v.dc) parts.push('<p class="tt-v-sub">' + th('verdict_dc', { dc: v.dc }) + '</p>');
    const dummies = dummyVars(activeCol(), m.n);
    if (dummies.length && dummies.length < m.n) {
        parts.push('<p class="tt-v-sub">' + th('dummy_vars', { list: rec(dummies.map(vText).join(', ')) }) + '</p>');
    }
    if (m.n > 10) parts.push('<p class="tt-v-sub warn">' + th('big_table', { n: m.n, rows: m.N }) + '</p>');
    box.innerHTML = parts.join('');
    box.hidden = false;
}

/* ── выбор функции при сравнении ─────────────────────────────────────────── */
function renderFnPicker() {
    const box = $('ttFnPick');
    const m = state.model;
    if (m.fns.length < 2) { box.hidden = true; box.innerHTML = ''; return; }
    box.innerHTML = '<span class="tt-fp-l">' + esc(t('analyze_fn')) + '</span>' + m.fns.map((f, k) => '<button type="button" class="tc-chip' + (k === state.active ? ' on' : '') + '" data-tt-fn="' + k + '" aria-pressed="' + (k === state.active) + '">' + fnLabel(k, true) + '</button>').join('');
    box.hidden = false;
}

/* ── вкладки ─────────────────────────────────────────────────────────────── */
const TABS = ['table', 'forms', 'kmap', 'alg', 'circuit'];
function setTab(tab, user) {
    if (TABS.indexOf(tab) < 0) tab = 'table';
    state.tab = tab;
    document.querySelectorAll('[data-tt-tab]').forEach((b) => {
        const on = b.getAttribute('data-tt-tab') === tab;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
    });
    TABS.forEach((x) => { $('ttPanel-' + x).hidden = x !== tab; });
    if (user) ae('tab', { tab });
    renderTab();
    saveHash();
}
function renderTab() {
    if (!state.model) return;
    const fn = { table: renderTable, forms: renderForms, kmap: renderKmap, alg: renderAlg, circuit: renderCircuit }[state.tab];
    guarded('tab_' + state.tab, fn);
    wideNeed();
    if (state.tab === 'table') wideHint();
}

/**
 * Нужна ли прибору ширина экрана: от пяти переменных или девяти столбцов таблицы. Признак — от САМОЙ функции,
 * а не от того, помещается ли таблица сейчас: иначе расширенная таблица «помещалась» бы, прибор сужался,
 * таблица снова не помещалась — и так по кругу. Ширину считает общий примитив (wide-workspace.js).
 */
function wideNeed() {
    const mark = $('ttWideNeed');
    if (!mark) return;
    const need = !!state.model && state.mode !== 'ege' && (state.model.n >= 5 || tableColumns().length >= 9);
    if (mark.hidden !== !need) {
        mark.hidden = !need;
        try { if (window.WideWorkspace) window.WideWorkspace.sync(); } catch (_) { /* примитив не загрузился — прибор остаётся в колонке */ }
    }
}

/**
 * Таблица шире своей рамки (телефон, 6–8 переменных): итоговый столбец уезжает за край. Говорим об этом
 * и предлагаем оставить только переменные и итог — одной кнопкой, без поиска переключателя.
 */
function wideHint() {
    const box = $('ttWide');
    if (!box) return;
    const wrap = document.querySelector('.tt-tablewrap');
    const wide = !!wrap && wrap.scrollWidth > wrap.clientWidth + 2 && !state.trainer;
    box.hidden = !wide;
    const btn = $('ttWideHide');
    if (btn) btn.hidden = !(wide && state.prefs.inter && tableColumns().some((c) => c.kind === 'act'));
}

/* ── таблица ─────────────────────────────────────────────────────────────── */
function tableColumns() {
    const m = state.model;
    const many = m.fns.length > 1;
    const cols = m.vars.map((v, i) => ({ kind: 'var', i, head: varHtml(v), text: varText(v), tex: docLatex({ k: 'var', name: v }) }));
    m.fns.forEach((f, k) => {
        if (state.prefs.inter && f.kind === 'expr') {
            for (const a of f.actions) {
                const last = a.node === f.ast;
                const formula = fHtml(a.node);
                cols.push({
                    kind: last ? 'fn' : 'act', k, num: a.num, col: a.col,
                    head: '<span class="tt-num">' + a.num + '</span>' + (last ? '<b>' + fnLabel(k, many) + '</b>' : formula),
                    title: fText(a.node),
                    text: last ? fnLabel(k, many) + ' = ' + fText(a.node) : fText(a.node),
                    tex: last ? fnLabel(k, many) : docLatex(toDoc(a.node, fo())),
                });
            }
            if (!f.actions.length) cols.push({ kind: 'fn', k, col: f.col, head: '<b>' + fnLabel(k, many) + '</b>', text: fnLabel(k, many), tex: fnLabel(k, many) });
        } else {
            const title = f.kind === 'expr' ? fText(f.ast) : f.vector;
            cols.push({ kind: 'fn', k, col: f.col, head: '<b>' + fnLabel(k, many) + '</b>', title, text: fnLabel(k, many), tex: fnLabel(k, many) });
        }
    });
    return cols;
}

function visibleRows() {
    const m = state.model;
    const out = [];
    const f = state.filter;
    const col = activeCol();
    const others = m.fns.map((x) => x.col);
    for (let q = 0; q < m.N; q++) {
        const r = curOrder() === 'desc' ? m.N - 1 - q : q;
        if (f === '1' && col[r] !== 1) continue;
        if (f === '0' && col[r] !== 0) continue;
        if (f === 'diff' && others.every((c) => c[r] === others[0][r])) continue;
        out.push(r);
    }
    return out;
}

function renderTable() {
    const m = state.model;
    const cols = tableColumns();
    const rows = visibleRows();
    const shown = rows.slice(0, state.shown);
    const tr = state.trainer;
    const clickable = state.mode === 'vector' && m.fns.length === 1;
    const lastVar = m.n - 1;
    const colClasses = cols.map((c, ci) => (c.kind === 'var' ? 'var' + (ci === lastVar ? ' lastvar' : '') : c.kind === 'fn' ? 'fn' : 'act'));
    let h = '<thead><tr><th scope="col" class="idx" title="' + esc(t('col_index_title')) + '">' + esc(t('col_index')) + '</th>';
    cols.forEach((c, ci) => {
        h += '<th scope="col" class="' + colClasses[ci] + '"' + (c.title ? ' title="' + esc(c.title) + '"' : '') + '><span class="tt-th" dir="ltr">' + c.head + '</span></th>';
    });
    h += '</tr></thead><tbody>';
    const diffRows = m.fns.length > 1 ? new Set(differences(m.fns[0].col, m.fns[m.fns.length - 1].col)) : null;
    for (const r of shown) {
        const bits = rowBits(r, m.n);
        const isDiff = diffRows && m.fns.slice(1).some((f) => f.col[r] !== m.fns[0].col[r]);
        h += '<tr' + (isDiff ? ' class="diff"' : '') + '><td class="idx">' + r + '</td>';
        cols.forEach((c, ci) => {
            const v = c.kind === 'var' ? bits[c.i] : c.col[r];
            const vc = v === 1 ? ' v1' : v === DC ? ' vd' : ' v0';
            if (tr && c.kind !== 'var') {
                const key = ci + ':' + r;
                const got = tr.answers.get(key);
                const mark = tr.checked ? (got === v ? ' ok' : ' bad') : '';
                h += '<td class="' + colClasses[ci] + ' tr' + mark + '"><button type="button" class="tt-cellbtn" data-tt-tr="' + key + '" aria-label="' + esc(t('trainer_cell', { row: r, col: c.text })) + '">' + (got == null ? '·' : esc(cellText(got))) + '</button></td>';
            } else if (clickable && c.kind === 'fn') {
                h += '<td class="' + colClasses[ci] + vc + '"><button type="button" class="tt-cellbtn" data-tt-set="' + r + '" aria-label="' + esc(t('set_cell', { row: r })) + '">' + esc(cellText(v)) + '</button></td>';
            } else {
                h += '<td class="' + colClasses[ci] + vc + '">' + esc(cellText(v)) + '</td>';
            }
        });
        h += '</tr>';
    }
    h += '</tbody>';
    $('ttTable').innerHTML = h;
    $('ttTable').classList.toggle('tt-inter', state.prefs.inter);
    const more = $('ttMore');
    more.hidden = rows.length <= state.shown;
    more.textContent = t('show_more', { n: Math.min(ROW_PAGE, rows.length - state.shown), left: rows.length - state.shown });
    const cap = $('ttTableCap');
    cap.textContent = state.filter === 'all' ? t('rows_all', { n: rows.length }) : t('rows_filtered', { n: rows.length, total: m.N });
    // Переключатели над таблицей отражают состояние.
    document.querySelectorAll('[data-tt-filter]').forEach((b) => {
        if (!b.closest('#ttTableTools')) return;
        const on = b.getAttribute('data-tt-filter') === state.filter;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $('ttFilterDiff').hidden = m.fns.length < 2;
    $('ttInterBtn').setAttribute('aria-pressed', state.prefs.inter ? 'true' : 'false');
    $('ttInterBtn').classList.toggle('on', state.prefs.inter);
    $('ttOrderBtn').textContent = ROW_ORDER[curOrder()];
    // Тренажёр.
    const canTrain = rows.length <= TRAINER_MAX && m.fns.length === 1;
    $('ttTrainerBtn').hidden = !canTrain && !tr;
    $('ttTrainerBtn').textContent = tr ? t('trainer_stop') : t('trainer_start');
    $('ttTrainerBar').hidden = !tr;
    if (tr) renderTrainerMsg();
    $('ttVecHint').hidden = !clickable;
}

function renderTrainerMsg() {
    const tr = state.trainer;
    const msg = $('ttTrainerMsg');
    if (!tr.checked) { msg.textContent = t('trainer_hint'); msg.className = 'tt-tr-msg'; return; }
    msg.textContent = tr.right === tr.total ? t('trainer_all_right', { n: tr.total }) : t('trainer_score', { right: tr.right, total: tr.total });
    msg.className = 'tt-tr-msg ' + (tr.right === tr.total ? 'good' : 'bad');
}

function toggleTrainer() {
    if (state.trainer) { state.trainer = null; renderTable(); ae('trainer_stop', {}); return; }
    state.trainer = { answers: new Map(), checked: false, right: 0, total: 0 };
    renderTable();
    ae('trainer_start', { n: state.model.n });
}
function checkTrainer() {
    const tr = state.trainer;
    if (!tr) return;
    const cols = tableColumns();
    const m = state.model;
    let right = 0;
    let total = 0;
    for (const r of visibleRows()) {
        cols.forEach((c, ci) => {
            if (c.kind === 'var') return;
            total++;
            if (tr.answers.get(ci + ':' + r) === c.col[r]) right++;
        });
    }
    tr.checked = true;
    tr.right = right;
    tr.total = total;
    renderTable();
    ae('trainer_check', { right, total, n: m.n, done: right === total ? 1 : 0 });
}

/* ── выгрузка таблицы ────────────────────────────────────────────────────── */
function tableData() {
    const cols = tableColumns();
    const m = state.model;
    const head = cols.map((c) => c.text);
    const rows = visibleRows().map((r) => {
        const bits = rowBits(r, m.n);
        return cols.map((c) => cellText(c.kind === 'var' ? bits[c.i] : c.col[r]));
    });
    return { cols, head, rows };
}

/** Запись в буфер — в том же стеке, что нажатие (Safari иначе отказывает); итог — тостом и событием. */
function copyOut(text, okPhrase, event, payload) {
    const p = window.__copyText ? window.__copyText(text) : Promise.resolve(false);
    Promise.resolve(p).then((ok) => {
        if (window.__toast) window.__toast(ok ? okPhrase : t('copy_failed'), ok ? 'success' : 'error');
        ae(event, Object.assign({ ok: ok ? 1 : 0 }, payload || {}));
    });
}
function copyTable() {
    const d = tableData();
    copyOut(toTsv(d.head, d.rows), t('copied_table'), 'copy_table', { rows: d.rows.length });
}
function copyFormat(fmt) {
    const d = tableData();
    const text = fmt === 'md' ? toMarkdown(d.head, d.rows) : toLatex(d.cols.map((c) => c.tex), d.rows, state.model.n);
    copyOut(text, t('copied_' + fmt), 'export', { fmt, what: 'table' });
}
function downloadCsv() {
    const d = tableData();
    // Разделитель — тот, с которым Excel страны открывает CSV (таблица записи).
    const blob = new Blob([toCsv(d.head, d.rows, NT.csv)], { type: 'text/csv;charset=utf-8' });
    save(blob, fileName('csv'));
    ae('export', { fmt: 'csv', what: 'table', rows: d.rows.length });
}
/** Имя файла — словами страницы: «truth-table.csv», «tablica-istinnosti.csv». */
function fileName(ext, what) {
    const base = what === 'kmap' ? t('file_kmap') : what === 'circuit' ? t('file_circuit') : t('file_table');
    try { if (window.__outputName) return window.__outputName.join(base, ext); } catch (_) { /* примитив не загрузился */ }
    return base + '.' + ext;
}
function save(blob, name) {
    if (window.__downloadFile) window.__downloadFile(blob, name);
}

/** Таблица картинкой: SVG с явными цветами → PNG. Длинная таблица — несколькими колонками рядом. */
function tableSvg() {
    const d = tableData();
    const font = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
    const measure = (s) => Math.max(34, 10 + String(s).length * 8.4);
    const widths = d.head.map((h, i) => Math.max(measure(h), ...d.rows.slice(0, 50).map((r) => measure(r[i]))));
    const rowH = 28;
    const gap = 18;
    const tw = widths.reduce((a, b) => a + b, 0);
    const { blocks, per } = tableBlocks(d.rows.length);
    const W = blocks * tw + (blocks - 1) * gap + 2;
    const H = (Math.min(per, d.rows.length) + 1) * rowH + 2;
    const out = ['<rect width="100%" height="100%" fill="#fff"/>'];
    for (let b = 0; b < blocks; b++) {
        const rows = d.rows.slice(b * per, (b + 1) * per);
        let x = 1 + b * (tw + gap);
        d.head.forEach((h, i) => {
            const fn = d.cols[i].kind === 'fn';
            out.push(`<rect x="${x}" y="1" width="${widths[i]}" height="${rowH}" fill="${fn ? '#e6ecfb' : '#f3f5f8'}" stroke="#c9d1dc"/>`);
            out.push(`<text x="${x + widths[i] / 2}" y="${1 + rowH / 2 + 5}" text-anchor="middle" font-weight="700" fill="#1e293b">${esc(h)}</text>`);
            rows.forEach((r, ri) => {
                const y = 1 + (ri + 1) * rowH;
                out.push(`<rect x="${x}" y="${y}" width="${widths[i]}" height="${rowH}" fill="${fn ? '#f2f5fd' : '#fff'}" stroke="#dde3ea"/>`);
                out.push(`<text x="${x + widths[i] / 2}" y="${y + rowH / 2 + 5}" text-anchor="middle" fill="#1e293b">${esc(r[i])}</text>`);
            });
            x += widths[i];
        });
    }
    return { svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="${font}" font-size="14">${out.join('')}</svg>`, width: W, height: H };
}

function svgToPng(svg, w, h, name, what) {
    // Масштаб 2 — пока холст в пределах iOS (4096 px по стороне, ~16 млн точек): иначе картинка выходит пустой.
    const scale = pngScale(w, h);
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
    const failed = (why) => {
        if (window.__toast) window.__toast(t('png_failed'), 'error');
        try { if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'png_failed', what, why, w: Math.round(w), h: Math.round(h) }); } catch (_) { /* телеметрия не мешает работе */ }
    };
    img.onload = () => {
        const cv = document.createElement('canvas');
        cv.width = Math.max(1, Math.round(w * scale));
        cv.height = Math.max(1, Math.round(h * scale));
        const g = cv.getContext('2d');
        g.fillStyle = '#fff';
        g.fillRect(0, 0, cv.width, cv.height);
        g.drawImage(img, 0, 0, cv.width, cv.height);
        URL.revokeObjectURL(url);
        /* the site's one encoder (timbrica-core.js): toBlob → OffscreenCanvas → data URL, and a
           rejection (CANVAS_ENCODE_FAILED) instead of a wait that never ends; started inside a
           promise, so even a throw (no core script) ends in the toast below */
        Promise.resolve().then(() => window.__canvasBlob(cv, 'image/png')).then((b) => {
            save(b, name);
            ae('export', { fmt: 'png', what, w: cv.width, h: cv.height });
        }, () => failed('encode'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); failed('image'); };
    img.src = url;
}

/* ── формы ───────────────────────────────────────────────────────────────── */
function cached(key, fn) {
    if (!(key in state.cache)) state.cache[key] = fn();
    return state.cache[key];
}
const minKey = (target) => 'min' + target + ':' + state.active;

function renderForms() {
    const m = state.model;
    const col = activeCol();
    const vars = m.vars;
    const plain = Uint8Array.from(col, (v) => (v === DC ? 0 : v));
    const hasDc = col.some((v) => v === DC);
    const html = [];
    const nums = (rows) => rows.map((r) => r).join(', ');
    // Формулу забирают в тетрадь и в отчёт: у каждой — своя кнопка «Копировать» (текст — в выбранной записи).
    state.formText = {};
    const copyBtn = (key, ast) => {
        state.formText[key] = fText(ast);
        return '<button type="button" class="tc-chip tt-copyf" data-tt-copyf="' + key + '">' + esc(t('copy')) + '</button>';
    };

    // СДНФ / СКНФ
    const sd = sdnf(plain, m.n);
    const sk = sknf(plain, m.n);
    const big = m.n > 6;
    html.push('<section class="tt-form"><h4>' + esc(t('sdnf')) + '</h4>');
    if (!sd.rows.length) html.push('<p class="tt-note">' + esc(t('sdnf_none')) + '</p>');
    else if (sd.rows.length > 64) html.push('<p class="tt-note">' + th('form_too_long', { n: sd.rows.length }) + '</p>');
    else {
        html.push('<p class="tt-formula" dir="ltr">' + fHtml(dnfAst(sd.cubes, vars), { tightAnd: true }) + '</p>' + copyBtn('sdnf', dnfAst(sd.cubes, vars)));
        html.push('<p class="tt-note">' + th('sdnf_from', { rows: rec(nums(sd.rows)) }) + '</p>');
        if (!big) html.push(termList(sd.rows, 'dnf'));
    }
    html.push('</section><section class="tt-form"><h4>' + esc(t('sknf')) + '</h4>');
    if (!sk.rows.length) html.push('<p class="tt-note">' + esc(t('sknf_none')) + '</p>');
    else if (sk.rows.length > 64) html.push('<p class="tt-note">' + th('form_too_long', { n: sk.rows.length }) + '</p>');
    else {
        html.push('<p class="tt-formula" dir="ltr">' + fHtml(cnfAst(sk.cubes, vars)) + '</p>' + copyBtn('sknf', cnfAst(sk.cubes, vars)));
        html.push('<p class="tt-note">' + th('sknf_from', { rows: rec(nums(sk.rows)) }) + '</p>');
        if (!big) html.push(termList(sk.rows, 'cnf'));
    }
    html.push('</section>');
    if (hasDc) html.push('<p class="tt-note">' + th('note_dc') + '</p>');

    // Минимальные формы
    html.push('<section class="tt-form"><h4>' + esc(t('min_forms')) + '</h4>');
    if (m.n > QMC_MAX_VARS) {
        html.push('<p class="tt-note">' + th('min_too_many', { max: QMC_MAX_VARS }) + '</p>');
    } else {
        const d = cached(minKey(1), () => minimize(col, m.n, 1));
        const c = cached(minKey(0), () => minimize(col, m.n, 0));
        if (!d.ok || !c.ok) html.push('<p class="tt-note">' + esc(t('min_too_big')) + '</p>');
        else {
            html.push(minLine(t('min_dnf'), dnfAst(d.cubes, vars), d, true) + copyBtn('min_dnf', dnfAst(d.cubes, vars)));
            html.push(minLine(t('min_cnf'), cnfAst(c.cubes, vars), c, false) + copyBtn('min_cnf', cnfAst(c.cubes, vars)));
            if (m.n <= 6) html.push(qmcSteps(d, 1));
        }
    }
    html.push('</section>');

    // Законы
    const fn = activeFn();
    if (fn.kind === 'expr' && m.n <= 8) {
        const res = cached('laws:' + state.active, () => simplifyByLaws(fn.ast, vars));
        html.push('<section class="tt-form"><h4>' + esc(t('laws_title')) + '</h4>' + lawsHtml(fn, res) + '</section>');
    }
    $('ttForms').innerHTML = html.join('');
}

function termList(rows, kind) {
    const m = state.model;
    const full = (1 << m.n) - 1;
    const items = rows.map((r) => {
        const ast = kind === 'dnf' ? dnfAst([{ mask: full, bits: r }], m.vars) : cnfAst([{ mask: full, bits: r }], m.vars);
        const set = rowBits(r, m.n).map((b) => cellText(b)).join('');
        return '<li><span class="tt-tl-r">' + esc(t('row_n', { n: r })) + ' <code dir="ltr">' + esc(set) + '</code></span><span class="tt-tl-f" dir="ltr">' + fHtml(ast, { tightAnd: true }) + '</span></li>';
    });
    return '<details class="tt-more"><summary>' + esc(kind === 'dnf' ? t('sdnf_terms') : t('sknf_terms')) + '</summary><ul class="tt-termlist">' + items.join('') + '</ul></details>';
}

function minLine(label, ast, res, isDnf) {
    const lits = literalCount(res.cubes);
    let s = '<div class="tt-min"><p class="tt-min-l">' + esc(label) + ' <span class="tt-min-c">' + th('literals', { n: lits }) + '</span></p>';
    s += '<p class="tt-formula" dir="ltr">' + fHtml(ast, { tightAnd: isDnf }) + '</p>';
    if (res.alternatives && res.alternatives.length) {
        const alt = res.alternatives.slice(0, 3).map((a) => '<p class="tt-formula alt" dir="ltr">' + fHtml(isDnf ? dnfAst(a, state.model.vars) : cnfAst(a, state.model.vars), { tightAnd: isDnf }) + '</p>').join('');
        s += '<p class="tt-note">' + th('min_alternatives', { n: res.alternatives.length }) + '</p>' + alt;
    }
    if (!res.exact) s += '<p class="tt-note warn">' + esc(t('min_not_exact')) + '</p>';
    return s + '</div>';
}

function qmcSteps(d, target) {
    const m = state.model;
    if (!d.stages || !d.stages.length) return '';
    const parts = [];
    d.stages.forEach((st, si) => {
        const rows = [];
        st.forEach((g) => {
            g.items.forEach((it, ii) => {
                rows.push('<tr' + (ii === 0 ? ' class="grp"' : '') + '><td>' + (ii === 0 ? g.w : '') + '</td><td dir="ltr"><code>' + esc(cubeString(it.cube, m.n)) + '</code></td><td dir="ltr">' + esc(it.from.join(', ')) + '</td><td>' + (it.used ? '✓' : '<b class="tt-prime">*</b>') + '</td></tr>');
            });
        });
        parts.push('<p class="tt-qm-h">' + (si === 0 ? th('qmc_stage0') : th('qmc_stage', { n: si })) + '</p>'
            + '<div class="tc-tablewrap"><table class="tt-mini"><thead><tr><th>' + esc(t('qmc_ones')) + '</th><th>' + esc(t('qmc_cube')) + '</th><th>' + esc(t('qmc_sets')) + '</th><th></th></tr></thead><tbody>' + rows.join('') + '</tbody></table></div>');
    });
    // Импликантная таблица.
    const ones = d.ones;
    let chart = '<div class="tc-tablewrap"><table class="tt-mini tt-chart"><thead><tr><th>' + esc(t('qmc_prime')) + '</th>' + ones.map((r) => '<th>' + r + '</th>').join('') + '</tr></thead><tbody>';
    d.primes.forEach((p, i) => {
        const ess = d.essential.indexOf(i) >= 0;
        const chosen = d.chosen.indexOf(i) >= 0;
        const f = target ? dnfAst([p], m.vars) : cnfAst([p], m.vars);
        chart += '<tr class="' + (chosen ? 'chosen' : '') + '"><td dir="ltr">' + fHtml(f, { tightAnd: true }) + (ess ? ' <span class="tt-core">' + esc(t('qmc_core_mark')) + '</span>' : '') + '</td>'
            + ones.map((r) => '<td>' + (covers(p, r) ? '×' : '') + '</td>').join('') + '</tr>';
    });
    chart += '</tbody></table></div>';
    parts.push('<p class="tt-qm-h">' + esc(t('qmc_chart')) + '</p>' + chart);
    parts.push('<p class="tt-note">' + th('qmc_core', { n: d.essential.length }) + (d.rest.length ? ' ' + th('qmc_rest', { rows: rec(d.rest.join(', ')) }) : '') + '</p>');
    return '<details class="tt-more"><summary>' + esc(t('qmc_title')) + '</summary><div class="tt-qm">' + parts.join('') + '</div></details>';
}

function lawsHtml(fn, res) {
    const m = state.model;
    const items = [];
    items.push('<li class="start"><span class="tt-law">' + esc(t('laws_start')) + '</span><span class="tt-formula" dir="ltr">' + fHtml(fn.ast) + '</span></li>');
    for (const s of res.steps) {
        const formulas = (LAW_FORMULAS[s.law] || []).map((src) => lawFormula(src)).join('; ');
        items.push('<li><span class="tt-law">' + esc(t('law_' + s.law)) + (formulas ? ' <span class="tt-law-f" dir="ltr">' + formulas + '</span>' : '') + '</span><span class="tt-formula" dir="ltr">= ' + fHtml(s.ast, { tightAnd: true }) + '</span></li>');
    }
    let tail = '';
    if (res.stopped === 'too_big') tail = t('laws_too_big');
    else if (res.stopped === 'check_failed' || res.stopped === 'steps') tail = t('laws_stopped');
    if (!res.steps.length && !tail) tail = t('laws_nothing');
    // Сравнение с минимальной ДНФ: законы не всегда доходят до минимума, и об этом надо сказать.
    const d = m.n <= QMC_MAX_VARS ? cached(minKey(1), () => minimize(activeCol(), m.n, 1)) : null;
    let verdictLine = '';
    if (d && d.ok && res.steps.length) {
        const mine = literalsIn(res.final);
        const best = literalCount(d.cubes);
        verdictLine = mine <= best ? th('laws_is_min') : th('laws_not_min', { a: mine, b: best });
    }
    return '<ol class="tt-laws">' + items.join('') + '</ol>'
        + (tail ? '<p class="tt-note">' + esc(tail) + '</p>' : '')
        + (verdictLine ? '<p class="tt-note">' + verdictLine + '</p>' : '');
}
function lawFormula(src) {
    const [l, r] = src.split(' = ');
    try {
        const a = parseInput(l, { locale: 'en' })[0].ast;
        const b = parseInput(r, { locale: 'en' })[0].ast;
        const o = { notation: state.prefs.notation, words: WORDS, sym: SIGNS, juxt: state.prefs.notation === 'eng' };
        return fmtHtml(a, o) + ' = ' + fmtHtml(b, o);
    } catch (_) { return esc(src); }
}

/* ── карта Карно ─────────────────────────────────────────────────────────── */
function renderKmap() {
    const m = state.model;
    const box = $('ttKmap');
    const L = kmapLayout(m.n);
    $('ttKmTarget').hidden = !L;
    if (!L) {
        box.innerHTML = '<p class="tt-note">' + esc(m.n < 2 ? t('kmap_few') : t('kmap_many')) + '</p>';
        $('ttKmTerms').innerHTML = '';
        return;
    }
    const target = state.prefs.kmTarget;
    document.querySelectorAll('[data-tt-km]').forEach((b) => {
        const on = +b.getAttribute('data-tt-km') === target;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const col = activeCol();
    const res = cached(minKey(target), () => minimize(col, m.n, target));
    const groups = (res.cubes || []).map((cube, i) => ({ cube, color: PALETTE[i % PALETTE.length] }));
    const names = m.vars.map(varText);
    const k = kmapSvg(L, col, names, groups, {
        values: [valLabels()[0], valLabels()[1], '–'],
        focus: state.kmFocus,
        cellLabel: (r, v) => t('kmap_cell', { row: r, v: cellText(v) }),
    });
    box.innerHTML = k.svg;
    const svg = box.querySelector('svg');
    if (svg) {
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', t('kmap_label'));
        svg.style.maxWidth = k.width + 'px';
    }
    // Члены минимальной формы — с цветом своей группы.
    const vars = m.vars;
    const terms = groups.map((g, i) => {
        const ast = target ? dnfAst([g.cube], vars) : cnfAst([g.cube], vars);
        const cells = cubeMinterms(g.cube, m.n).filter((r) => col[r] === target);
        return '<li><button type="button" class="tt-term" data-tt-group="' + i + '" style="--g:' + g.color + '" aria-pressed="' + (state.kmFocus === i) + '"><i></i><span dir="ltr">' + fHtml(ast, { tightAnd: true }) + '</span><small>' + th('kmap_cells', { list: rec(cells.join(', ')) }) + '</small></button></li>';
    });
    const whole = target ? dnfAst(res.cubes || [], vars) : cnfAst(res.cubes || [], vars);
    $('ttKmTerms').innerHTML = '<p class="tt-min-l">' + esc(target ? t('min_dnf') : t('min_cnf')) + '</p><p class="tt-formula" dir="ltr">' + fHtml(whole, { tightAnd: !!target }) + '</p>'
        + (terms.length ? '<ul class="tt-terms">' + terms.join('') + '</ul>' : '')
        + '<p class="tt-note">' + esc(t('kmap_click')) + '</p>';
}

function kmapExport(fmt) {
    const m = state.model;
    const L = kmapLayout(m.n);
    if (!L) return;
    const target = state.prefs.kmTarget;
    const res = cached(minKey(target), () => minimize(activeCol(), m.n, target));
    const groups = (res.cubes || []).map((cube, i) => ({ cube, color: PALETTE[i % PALETTE.length] }));
    const k = kmapSvg(L, activeCol(), m.vars.map(varText), groups, {
        values: [valLabels()[0], valLabels()[1], '–'],
        explicit: { ink: '#1e293b', muted: '#5b6675', line: '#9aa6b5', bg: '#ffffff', one: '#eef3ff' },
    });
    if (fmt === 'svg') { save(new Blob([k.svg], { type: 'image/svg+xml' }), fileName('svg', 'kmap')); ae('export', { fmt: 'svg', what: 'kmap' }); }
    else svgToPng(k.svg, k.width, k.height, fileName('png', 'kmap'), 'kmap');
}

/* ── обратный путь: клик меняет функцию ──────────────────────────────────── */
function toggleCell(r) {
    const m = state.model;
    if (!m || m.fns.length !== 1) return;
    const col = Uint8Array.from(activeCol());
    col[r] = col[r] === 0 ? 1 : col[r] === 1 ? DC : 0;
    const vec = vectorString(col);
    if (state.mode !== 'vector') {
        state.fromExpr = $('ttInput').value;
        setMode('vector', { keep: true });
    }
    $('ttVecNames').value = m.vars.join(', ');
    $('ttVector').value = vec;
    $('ttVecN').value = String(m.n);
    syncVecCount();
    rebuild(true);
    $('ttRestore').hidden = !state.fromExpr;
    ae('cell_toggle', { n: m.n, tab: state.tab });
}

/* ── Жегалкин и Пост ─────────────────────────────────────────────────────── */
function renderAlg() {
    const m = state.model;
    const col = Uint8Array.from(activeCol(), (v) => (v === DC ? 0 : v));
    const html = [];
    const terms = zhegalkinTerms(col, m.n);
    const poly = terms.length ? terms.map((tm) => tm.vars.length ? tm.vars.map((i) => varHtml(m.vars[i])).join('') : '1').join(' ⊕ ') : '0';
    html.push('<section class="tt-form"><h4>' + esc(t('zh_title')) + '</h4><p class="tt-formula" dir="ltr">' + poly + '</p>');
    if (m.n <= 5) {
        const tri = zhegalkinTriangle(col);
        const rows = tri.map((row, k) => {
            const mono = rowBits(k, m.n).map((b, i) => (b ? m.vars[i] : '')).filter(Boolean);
            const label = mono.length ? mono.map(varHtml).join('') : '1';
            return '<tr><td class="lbl" dir="ltr">' + label + '</td>' + row.map((v, j) => '<td class="' + (j === 0 ? 'edge' + (v ? ' on' : '') : '') + '">' + v + '</td>').join('') + '</tr>';
        }).join('');
        html.push('<details class="tt-more"' + (m.n <= 3 ? ' open' : '') + '><summary>' + esc(t('zh_triangle')) + '</summary><p class="tt-note">' + esc(t('zh_triangle_hint')) + '</p><div class="tc-tablewrap"><table class="tt-tri" dir="ltr">' + rows + '</table></div></details>');
    }
    if (col.length !== activeCol().length || activeCol().some((v) => v === DC)) html.push('<p class="tt-note">' + th('note_dc') + '</p>');
    html.push('</section>');

    const pc = postClasses(col, m.n);
    const setText = (r) => '(' + rowBits(r, m.n).map((b) => b).join(', ') + ')';
    // «F(0, 0, 0) = 0» — запись без единого слова: её пишет код (и на всех наборах
    // из одних нулей или единиц она одна для всех языков).
    const fAt = (bit, value) => '<bdi dir="ltr">F(' + (m.n <= 4 ? new Array(m.n).fill(bit).join(', ') : bit + ', …, ' + bit) + ') = ' + value + '</bdi>';
    const why = {
        T0: fAt(0, pc.T0.in ? 0 : 1),
        T1: fAt(1, pc.T1.in ? 1 : 0),
        S: pc.S.in ? th('post_s_in') : th('post_s_out', { a: rec(setText(pc.S.witness.a)), b: rec(setText(pc.S.witness.b)), v: pc.S.witness.value }),
        M: pc.M.in ? th('post_m_in') : th('post_m_out', { a: rec(setText(pc.M.witness.a)), b: rec(setText(pc.M.witness.b)) }),
        L: pc.L.in ? th('post_l_in') : th('post_l_out', { term: rec(pc.L.witness.term.vars.map((i) => varText(m.vars[i])).join('')) }),
    };
    const rows = ['T0', 'T1', 'S', 'M', 'L'].map((k) => '<tr><th scope="row">' + esc(t('post_' + k.toLowerCase())) + '</th><td class="' + (pc[k].in ? 'yes' : 'no') + '">' + (pc[k].in ? '✓' : '✗') + '</td><td>' + why[k] + '</td></tr>').join('');
    html.push('<section class="tt-form"><h4>' + esc(t('post_title')) + '</h4><div class="tc-tablewrap"><table class="tt-post">' + rows + '</table></div>'
        + '<p class="tt-note' + (pc.complete ? ' good' : '') + '">' + esc(pc.complete ? t('post_complete') : t('post_not_complete')) + '</p></section>');
    const dummies = dummyVars(activeCol(), m.n);
    html.push('<section class="tt-form"><h4>' + esc(t('dummy_title')) + '</h4><p class="tt-note">' + (dummies.length ? th('dummy_vars', { list: rec(dummies.map(vText).join(', ')) }) : th('dummy_none')) + '</p></section>');
    $('ttAlg').innerHTML = html.join('');
}

/* ── схема ───────────────────────────────────────────────────────────────── */
function circuitAst() {
    const m = state.model;
    const fn = activeFn();
    let src = state.prefs.circSrc;
    if (src === 'expr' && fn.kind !== 'expr') src = 'dnf';
    if (src === 'expr') return { ast: fn.ast, src };
    if (m.n > QMC_MAX_VARS) return { ast: null, src };
    const target = src === 'dnf' ? 1 : 0;
    const res = cached(minKey(target), () => minimize(activeCol(), m.n, target));
    if (!res.ok) return { ast: null, src };
    return { ast: target ? dnfAst(res.cubes, m.vars) : cnfAst(res.cubes, m.vars), src };
}
function renderCircuit() {
    const box = $('ttCircuit');
    const { ast, src } = circuitAst();
    document.querySelectorAll('[data-tt-csrc]').forEach((b) => {
        const on = b.getAttribute('data-tt-csrc') === src;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        if (b.getAttribute('data-tt-csrc') === 'expr') b.disabled = activeFn().kind !== 'expr';
    });
    document.querySelectorAll('[data-tt-gate]').forEach((b) => {
        const on = b.getAttribute('data-tt-gate') === state.prefs.gate;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (!ast) { box.innerHTML = '<p class="tt-note">' + esc(t('min_too_big')) + '</p>'; $('ttCircuitNote').textContent = ''; return; }
    if (ast.t === 'const') { box.innerHTML = '<p class="tt-note">' + th('circuit_const', { v: ast.v }) + '</p>'; $('ttCircuitNote').textContent = ''; return; }
    const count = gateCount(ast);
    if (count > 60) { box.innerHTML = '<p class="tt-note">' + th('circuit_big', { n: count }) + '</p>'; $('ttCircuitNote').textContent = ''; return; }
    const c = circuitSvg(ast, state.model.vars, { style: state.prefs.gate, label: 'F', bg: 'var(--tt-paper)' });
    box.innerHTML = c.svg;
    const svg = box.querySelector('svg');
    if (svg) {
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', t('circuit_label', { n: count }));
        // По ширине коробки, но не крупнее натуральной и не мельче 60 %: мельче
        // подписи не прочитать — тогда коробка прокручивается вбок.
        svg.style.width = '100%';
        svg.style.maxWidth = c.width + 'px';
        svg.style.minWidth = Math.round(c.width * 0.6) + 'px';
    }
    $('ttCircuitNote').innerHTML = th('circuit_note', { n: count, f: sampleFormula('¬A ∨ B') });
}
function circuitExport(fmt) {
    const { ast } = circuitAst();
    if (!ast || ast.t === 'const') return;
    const c = circuitSvg(ast, state.model.vars, { style: state.prefs.gate, label: 'F', bg: '#ffffff', color: '#1e293b' });
    const name = fileName(fmt, 'circuit');
    if (fmt === 'svg') { save(new Blob([c.svg], { type: 'image/svg+xml' }), name); ae('export', { fmt: 'svg', what: 'circuit' }); }
    else svgToPng(c.svg, c.width, c.height, name, 'circuit');
}

/* ── помощник ЕГЭ ────────────────────────────────────────────────────────── */
function egeVars() {
    return state.model ? state.model.vars : [];
}
function ensureEgeRows() {
    const n = Math.max(2, egeVars().length || 4);
    if (!state.ege.rows.length) state.ege.rows = [0, 1, 2].map(() => ({ cells: new Array(n).fill(null), f: 0 }));
    for (const row of state.ege.rows) {
        while (row.cells.length < n) row.cells.push(null);
        if (row.cells.length > n) row.cells.length = n;
    }
}
function renderEgeEditor() {
    ensureEgeRows();
    const n = state.ege.rows[0].cells.length;
    let h = '<thead><tr>' + Array.from({ length: n }, (_, c) => '<th scope="col">?<sub>' + (c + 1) + '</sub></th>').join('') + '<th scope="col" class="fn">F</th></tr></thead><tbody>';
    state.ege.rows.forEach((row, i) => {
        h += '<tr>' + row.cells.map((v, c) => '<td><button type="button" class="tt-egecell" data-tt-eg="' + i + ':' + c + '" aria-label="' + esc(t('ege_cell', { row: i + 1, col: c + 1 })) + '">' + (v == null ? '' : v) + '</button></td>').join('')
            + '<td class="fn"><button type="button" class="tt-egecell" data-tt-eg="' + i + ':f" aria-label="' + esc(t('ege_cell_f', { row: i + 1 })) + '">' + row.f + '</button></td></tr>';
    });
    $('ttEgeGrid').innerHTML = h + '</tbody>';
    $('ttEgeDel').disabled = state.ege.rows.length <= 1;
    $('ttEgeAdd').disabled = state.ege.rows.length >= 8;
}

function renderEge() {
    renderEgeEditor();
    const out = $('ttEgeOut');
    const panel = $('ttEgePanel');
    panel.hidden = false;
    const egeRefuse = (issue, extra) => { if (state.byUser && state.src.trim()) refused('ege_' + issue, state.src, Object.assign({ mode: 'ege' }, extra || {})); };
    if (!state.model) { out.innerHTML = '<p class="tt-note">' + esc(t('ege_need_expr')) + '</p>'; return; }
    const vars = state.model.vars;
    const n = vars.length;
    const fn = state.model.fns[0];
    if (fn.kind !== 'expr' || state.model.fns.length > 1) { out.innerHTML = '<p class="tt-note">' + esc(t('ege_one_expr')) + '</p>'; egeRefuse('not_one_expr', { fns: state.model.fns.length }); return; }
    if (n < 2 || n > 6) { out.innerHTML = '<p class="tt-note">' + esc(t('ege_vars_range')) + '</p>'; egeRefuse('vars_range', { n }); return; }
    const rows = state.ege.rows;
    if (rows.every((r) => r.cells.every((c) => c == null))) { out.innerHTML = '<p class="tt-note">' + esc(t('ege_fill_hint')) + '</p>'; return; }
    if (state.ege.hidden) {
        out.innerHTML = '<p class="tc-msg">' + esc(t('ege_hidden')) + '</p><button type="button" class="tc-btn sm" id="ttEgeReveal">' + esc(t('ege_reveal')) + '</button>';
        return;
    }
    const res = solveEge({ ast: fn.ast, vars, rows });
    const letter = (v) => varText(vars[v]);
    const permText = (p) => p.map(letter).join('');
    const html = [];
    if (res.solutions.length === 1) {
        const s = res.solutions[0];
        html.push('<div class="tt-ege-ans"><p class="tc-answer-l">' + esc(t('ege_answer')) + '</p><p class="tt-ege-letters" dir="ltr">' + esc(permText(s.perm)) + '</p><button type="button" class="tc-btn sm ghost" id="ttEgeCopy">' + esc(t('copy')) + '</button></div>');
    } else if (res.solutions.length > 1) {
        html.push('<div class="tt-ege-ans"><p class="tc-answer-l">' + th('ege_many_title', { n: res.solutions.length }) + '</p><p class="tt-ege-letters sm" dir="ltr">' + res.solutions.slice(0, 12).map((s) => esc(permText(s.perm))).join(', ') + '</p></div>');
    } else {
        html.push('<p class="tc-msg bad">' + esc(t('ege_none')) + '</p>');
    }
    // Заполненный фрагмент.
    if (res.solutions.length) {
        const s = res.solutions[0];
        const head = res.solutions.length === 1 ? s.perm.map((v) => '<th scope="col">' + varHtml(vars[v]) + '</th>').join('') : s.perm.map((_, c) => '<th scope="col">?<sub>' + (c + 1) + '</sub></th>').join('');
        let tb = '<table class="tt-mini tt-ege-fill" dir="ltr"><thead><tr>' + head + '<th scope="col" class="fn">F</th></tr></thead><tbody>';
        rows.forEach((row, i) => {
            tb += '<tr>' + row.cells.map((v, c) => {
                if (v != null) return '<td>' + v + '</td>';
                const all = res.solutions.map((x) => x.fill[i][c]);
                const same = all.every((x) => x === all[0]) ? all[0] : null;
                return same == null ? '<td class="amb">0/1</td>' : '<td class="filled">' + same + '</td>';
            }).join('') + '<td class="fn">' + row.f + '</td></tr>';
        });
        tb += '</tbody></table>';
        html.push('<p class="tt-min-l">' + esc(t('ege_filled')) + '</p><div class="tc-tablewrap">' + tb + '</div><p class="tt-note">' + th('ege_filled_hint', { any: rec('0/1') }) + '</p>');
    }
    html.push('<p class="tt-min-l">' + esc(t('ege_reasoning')) + '</p><ol class="tt-reason">' + res.explain.map((st) => '<li>' + egeStep(st, vars, rows) + '</li>').join('') + '</ol>');
    out.innerHTML = html.join('');
    const copyBtn = $('ttEgeCopy');
    if (copyBtn) {
        copyBtn.addEventListener('click', () => copyOut(permText(res.solutions[0].perm), t('copied'), 'ege_copy', {}));
    }
    if (state.byUser) {
        const grid = rows.map((r) => r.cells.map((c) => (c == null ? '.' : c)).join('') + ':' + r.f).join(',');
        if (!res.solutions.length) egeRefuse('no_solution', { n, rows: rows.length });
        else resultShown({ mode: 'ege', n, rows: rows.length, solutions: res.solutions.length, sample: state.ege.sample >= 0 ? 1 : 0 }, 'ege|' + state.src.trim() + '|' + grid);
    }
}

function egeStep(st, vars, rows) {
    const n = vars.length;
    const L = (v) => varText(vars[v]);
    const setTuple = (r) => '(' + rowBits(r, n).join(', ') + ')';
    const varsTuple = '(' + vars.map(varText).join(', ') + ')';
    const rowCells = (i) => rows[i].cells.map((c) => (c == null ? '·' : c)).join(' ');
    const perm = (p) => rec(p.map(L).join(''));
    const orVars = (list) => list.map(L).join(t('ege_or'));
    // «столбец 2 — z; столбец 3 — x или y»: и слова, и разделитель — из перевода.
    const colList = (list) => list.map((c) => (c.vars.length
        ? t('ege_col_is', { col: c.col + 1, vars: orVars(c.vars) })
        : t('ege_col_none', { col: c.col + 1 }))).join(t('ege_cols_sep'));
    switch (st.k) {
        case 'sets': {
            if (!st.sets) return th('ege_sets_n', { feq: fEq(st.f), count: st.count, total: st.total });
            return th('ege_sets', { feq: fEq(st.f), count: st.count, total: st.total })
                + '<div class="tc-tablewrap"><table class="tt-mini tt-sets" dir="ltr"><thead><tr>' + vars.map((v) => '<th>' + varHtml(v) + '</th>').join('') + '<th class="fn">F</th></tr></thead><tbody>'
                + st.sets.map((r) => '<tr>' + rowBits(r, n).map((b) => '<td>' + b + '</td>').join('') + '<td class="fn">' + st.f + '</td></tr>').join('') + '</tbody></table></div>';
        }
        // «x = 1» — одна запись: по частям арабская фраза может поставить значение перед именем.
        case 'fact': return th('ege_fact', { feq: fEq(st.f), eq: rec(L(st.v) + ' = ' + st.b) });
        case 'elim': return th('ege_elim', { row: st.row + 1, col: st.col + 1, val: st.val, feq: fEq(st.f), v: rec(L(st.v)), eq: rec(L(st.v) + ' = ' + st.b) });
        case 'all_sets': return th('ege_all_sets', { feq: fEq(st.f), count: st.count });
        case 'colcount': return th('ege_colcount', { col: st.col + 1, ones: st.ones, zeros: st.zeros, feq: fEq(st.f), vars: orVars(st.vars) });
        case 'row': {
            // Наборов больше шести — перечислять их незачем, называем число. Набор и
            // его значения («(w, x, y, z) = (1, 0, 0, 1)») — одна запись: по частям
            // арабская строка поставила бы значения перед именами.
            const row = st.row + 1;
            const cells = rec(rowCells(st.row));
            const cols = colList(st.cols);
            if (!st.sets) return th('ege_row_n', { row, cells, feq: fEq(st.f), count: st.count, cols });
            const match = rec(varsTuple + ' = ' + st.sets.map(setTuple).join(', '));
            return st.count === 1 ? th('ege_row_one', { row, cells, feq: fEq(st.f), match, cols }) : th('ege_row', { row, cells, feq: fEq(st.f), match, cols });
        }
        case 'single_col': return th('ege_single_col', { col: st.col + 1, v: rec(L(st.v)) });
        case 'single_var': return th('ege_single_var', { col: st.col + 1, v: rec(L(st.v)) });
        case 'all_known': return th('ege_all_known', { perm: perm(st.perm) });
        case 'try': return th('ege_try', { count: st.count });
        case 'try_ok': return th('ege_try_ok', { perm: perm(st.perm) });
        case 'try_fail': return th('ege_try_fail', { perm: perm(st.perm), row: st.row + 1 });
        case 'try_dup': return th('ege_try_dup', { perm: perm(st.perm) });
        case 'answer': return '<b>' + th('ege_final', { answer: perm(st.perm) }) + '</b>';
        case 'many': return th('ege_many', { count: st.count });
        case 'none': return th('ege_none');
        default: return '';
    }
}

function loadEgeSample(i) {
    const s = EGE_SAMPLES[i];
    if (!s) return;
    state.ege.sample = i;
    state.ege.hidden = false;
    $('ttInput').value = inSchoolSigns(s.expr, SIGNS);
    state.ege.rows = s.rows.map((r) => ({ cells: r.cells.slice(), f: r.f }));
    rebuild(true);
    ae('ege_sample', { i });
}
function newEgeTask() {
    const seed = (() => { try { return crypto.getRandomValues(new Uint32Array(1))[0]; } catch (_) { return Math.floor(Math.random() * 4294967295); } })();
    let s = seed >>> 0;
    const rnd = () => { s = (s + 0x6D2B79F5) >>> 0; let x = s; x = Math.imul(x ^ (x >>> 15), x | 1); x ^= x + Math.imul(x ^ (x >>> 7), x | 61); return ((x ^ (x >>> 14)) >>> 0) / 4294967296; };
    const task = randomEgeTask(rnd, (src) => parseInput(src, { locale: 'en' })[0].ast);
    if (!task) return;
    state.ege.sample = -1;
    $('ttInput').value = inSchoolSigns(task.expr, SIGNS);
    state.ege.rows = task.rows.map((r) => ({ cells: r.cells.slice(), f: r.f }));
    // Новая задача — без готового решения: человек сначала решает сам.
    state.ege.hidden = true;
    rebuild(true);
    ae('ege_new', {});
}

/* ── режимы ──────────────────────────────────────────────────────────────── */
function setMode(mode, opts = {}) {
    state.mode = mode;
    document.querySelectorAll('[data-tt-mode]').forEach((b) => {
        const on = b.getAttribute('data-tt-mode') === mode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    $('ttExprBox').hidden = mode === 'vector';
    $('ttVecBox').hidden = mode !== 'vector';
    $('ttEgeBox').hidden = mode !== 'ege';
    $('ttEgePanel').hidden = mode !== 'ege';
    $('ttExprSamples').hidden = mode !== 'expr';
    drawRecent();
    let firstKey = true;
    document.querySelectorAll('[data-tt-for]').forEach((b) => {
        b.hidden = b.getAttribute('data-tt-for').split(' ').indexOf(mode) < 0;
        // одна остановка Tab на группу клавиш: первая видимая
        b.tabIndex = !b.hidden && firstKey ? 0 : -1;
        if (!b.hidden) firstKey = false;
    });
    if (mode !== 'vector') $('ttRestore').hidden = true;
    if (mode === 'vector' && !opts.keep && !$('ttVector').value) {
        // Пустой вектор ничего не показывает: начинаем с функции, которую видно.
        $('ttVecN').value = '3';
        $('ttVecNames').value = 'A, B, C';
        $('ttVector').value = '00010111';
        syncVecCount();
    }
    if (mode === 'ege' && !opts.keep) {
        if (state.ege.sample < 0 && !state.ege.rows.some((r) => r.cells.some((c) => c != null))) {
            loadEgeSample(0);
            return;
        }
    }
    if (!opts.silent) ae('mode', { mode });
    if (!opts.keep) rebuild(true);
}

function syncVecCount() {
    const n = Math.max(1, Math.min(8, parseInt($('ttVecN').value, 10) || 3));
    const names = $('ttVecNames').value.split(/[\s,;]+/).filter(Boolean);
    const want = 1 << n;
    let vec = $('ttVector').value.replace(/[^01\-]/g, '');
    if (vec.length !== want) vec = (vec + '0'.repeat(want)).slice(0, want);
    $('ttVector').value = vec;
    if (names.length !== n) {
        const base = n <= 6 ? ['A', 'B', 'C', 'D', 'E', 'G'] : Array.from({ length: n }, (_, i) => 'x' + (i + 1));
        $('ttVecNames').value = base.slice(0, n).join(', ');
    }
    $('ttVecLen').textContent = t('vec_len', { n: want });
}

/* ── адрес ───────────────────────────────────────────────────────────────── */
function saveHash() {
    const q = new URLSearchParams();
    if (state.mode === 'vector') {
        q.set('v', $('ttVector').value);
        q.set('a', $('ttVecNames').value.replace(/\s+/g, ''));
    } else {
        q.set('e', $('ttInput').value);
    }
    if (state.mode === 'ege') {
        q.set('m', 'ege');
        q.set('g', state.ege.rows.map((r) => r.cells.map((c) => (c == null ? '.' : c)).join('') + ':' + r.f).join(','));
    } else if (state.tab !== 'table') q.set('t', state.tab);
    try { history.replaceState(null, '', '#' + q.toString()); } catch (_) { /* песочница */ }
}
function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    if (q.get('t')) state.tab = q.get('t');
    if (q.get('v') != null) {
        $('ttVector').value = q.get('v');
        $('ttVecNames').value = (q.get('a') || '').split(',').join(', ');
        $('ttVecN').value = String(Math.max(1, Math.round(Math.log2(Math.max(2, q.get('v').length)))));
        setMode('vector', { keep: true, silent: true });
        return true;
    }
    if (q.get('e') == null) return false;
    $('ttInput').value = q.get('e');
    if (q.get('m') === 'ege') {
        const g = q.get('g') || '';
        state.ege.rows = g.split(',').filter(Boolean).map((s) => {
            const [cells, f] = s.split(':');
            return { cells: cells.split('').map((c) => (c === '0' ? 0 : c === '1' ? 1 : null)), f: f === '1' ? 1 : 0 };
        });
        state.ege.sample = -2;
        setMode('ege', { keep: true, silent: true });
    }
    return true;
}

function share() {
    saveHash();
    const url = location.href;
    // Окно «Поделиться» и буфер обмена Safari отдаёт только в том же стеке, что нажатие: вызовы — сразу.
    if (navigator.share && matchMedia('(pointer:coarse)').matches) {
        let asked = null;
        try { asked = navigator.share({ title: document.title, url }); } catch (_) { asked = null; }
        if (asked && asked.then) {
            asked.then(() => ae('share', { via: 'native', ok: 1 }), (e) => {
                if (e && e.name === 'AbortError') { ae('share', { via: 'native', ok: 0 }); return; }
                copyOut(url, t('link_copied'), 'share', { via: 'clipboard' });
            });
            return;
        }
    }
    copyOut(url, t('link_copied'), 'share', { via: 'clipboard' });
}

/* ── PDF ─────────────────────────────────────────────────────────────────── */
function buildReport() {
    const m = state.model;
    const root = document.createElement('div');
    root.className = 'lg-app tt tt-report';
    root.style.cssText = 'padding:0;background:#fff;max-width:none;color:#1e293b';
    const add = (tag, cls, html) => { const el = document.createElement(tag); if (cls) el.className = cls; el.innerHTML = html; root.appendChild(el); return el; };
    add('div', 'tt-read', readingHtml());
    add('div', 'tt-verdict', $('ttVerdict').innerHTML);
    // Таблица — до 64 строк на листе; длиннее — в CSV.
    const d = tableData();
    const cols = d.cols;
    let tb = '<table class="tt-table tt-print-table"><thead><tr>' + cols.map((c) => '<th>' + '<span class="tt-th" dir="ltr">' + c.head + '</span></th>').join('') + '</tr></thead><tbody>';
    d.rows.slice(0, 64).forEach((r) => { tb += '<tr>' + r.map((v, i) => '<td class="' + (cols[i].kind === 'fn' ? 'fn' : '') + '">' + esc(v) + '</td>').join('') + '</tr>'; });
    tb += '</tbody></table>';
    add('h3', 'tt-rep-h', esc(t('tab_table')));
    add('div', '', tb).dir = 'ltr';
    if (d.rows.length > 64) add('p', 'tt-note', th('pdf_rows_cut', { shown: 64, n: d.rows.length }));
    // Формы.
    renderForms();
    add('h3', 'tt-rep-h', esc(t('tab_forms')));
    const forms = $('ttForms').cloneNode(true);
    forms.removeAttribute('id');
    forms.querySelectorAll('details').forEach((dd) => { dd.open = false; });
    forms.querySelectorAll('.tt-copyf').forEach((b) => b.remove());
    root.appendChild(forms);
    const L = kmapLayout(m.n);
    if (L) {
        const res = cached(minKey(1), () => minimize(activeCol(), m.n, 1));
        const k = kmapSvg(L, activeCol(), m.vars.map(varText), (res.cubes || []).map((cube, i) => ({ cube, color: PALETTE[i % PALETTE.length] })), {
            values: [valLabels()[0], valLabels()[1], '–'],
            explicit: { ink: '#1e293b', muted: '#5b6675', line: '#9aa6b5', bg: '#ffffff', one: '#eef3ff' },
        });
        add('h3', 'tt-rep-h', esc(t('tab_kmap')));
        add('div', '', k.svg).dir = 'ltr';
    }
    renderTab();
    return root;
}
function savePdf() {
    if (!state.model || !pdfReady()) return;
    const btn = $('ttPdf');
    btn.disabled = true;
    window.__generatePDF({
        element: buildReport(),
        title: t('pdf_title'),
        filename: fileName('pdf'),
        strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => {
        btn.disabled = false;
        ae('pdf', { ok: !!(r && r.ok), code: r && r.code });
    }, () => { btn.disabled = false; });
}

/* ── недавние выражения ───────────────────────────────────────────────────── */
const RECENT_MAX = 6;
let recent = [];
function pushRecent(src) {
    if (!src || src.length > 200) return;
    recent = [src].concat(recent.filter((x) => x !== src)).slice(0, RECENT_MAX);
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'recent', recent); } catch (_) { /* хранилище недоступно */ }
    drawRecent();
}
function drawRecent() {
    const box = $('ttRecent');
    if (!box) return;
    box.hidden = !recent.length || state.mode !== 'expr';
    $('ttRecentList').innerHTML = recent.map((x, i) => '<button type="button" class="tc-chip tt-mono" dir="ltr" data-tt-recent="' + i + '" title="' + esc(x) + '">'
        + esc(x.length > 34 ? x.slice(0, 33) + '…' : x) + '</button>').join('');
}

/* ── ввод ────────────────────────────────────────────────────────────────── */
function insertAtCaret(field, ins) {
    const a = field.selectionStart == null ? field.value.length : field.selectionStart;
    const z = field.selectionEnd == null ? a : field.selectionEnd;
    field.value = field.value.slice(0, a) + ins + field.value.slice(z);
    const pos = a + ins.length;
    try { field.setSelectionRange(pos, pos); } catch (_) { /* поле без выделения */ }
    field.focus();
}

function bind() {
    const input = $('ttInput');
    let timer = 0;
    const later = () => { clearTimeout(timer); timer = setTimeout(() => rebuild(true), 260); };
    input.addEventListener('input', later);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); clearTimeout(timer); rebuild(true); } });
    $('ttVector').addEventListener('input', later);
    $('ttVecNames').addEventListener('input', later);
    $('ttVecN').addEventListener('change', () => { syncVecCount(); rebuild(true); });

    document.querySelectorAll('[data-tt-key]').forEach((b) => {
        b.addEventListener('click', (e) => {
            e.preventDefault();
            const k = b.getAttribute('data-tt-key');
            const field = state.mode === 'vector' ? $('ttVector') : input;
            if (k === '⌫') {
                const a = field.selectionStart == null ? field.value.length : field.selectionStart;
                const z = field.selectionEnd == null ? a : field.selectionEnd;
                if (a === z && a > 0) { field.value = field.value.slice(0, a - 1) + field.value.slice(z); try { field.setSelectionRange(a - 1, a - 1); } catch (_) { /* поле без выделения */ } }
                else field.value = field.value.slice(0, a) + field.value.slice(z);
                field.focus();
            } else insertAtCaret(field, k);
            clearTimeout(timer);
            rebuild(true);
            ae('key', { k });
        });
    });
    // Экранные клавиши — ОДНА остановка Tab на всю группу, внутри ходят стрелки (как в панели инструментов):
    // иначе путь с клавиатуры от поля до примеров и результата — двадцать одно нажатие Tab.
    $('ttKeys').addEventListener('keydown', (e) => {
        if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].indexOf(e.key) < 0) return;
        const v = Array.from(document.querySelectorAll('#ttKeys [data-tt-key]')).filter((k) => !k.hidden);
        const i = v.indexOf(document.activeElement);
        if (i < 0) return;
        const j = e.key === 'Home' ? 0 : e.key === 'End' ? v.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + v.length) % v.length;
        v.forEach((k) => { k.tabIndex = -1; });
        v[j].tabIndex = 0;
        v[j].focus();
        e.preventDefault();
    });
    $('ttCompare').addEventListener('click', () => {
        const v = input.value.replace(/\s*;?\s*$/, '');
        input.value = v + '; ';
        input.focus();
        try { input.setSelectionRange(input.value.length, input.value.length); } catch (_) { /* поле без выделения */ }
        ae('compare_open', {});
    });
    document.querySelectorAll('[data-tt-sample]').forEach((b) => {
        b.addEventListener('click', () => {
            if (state.mode !== 'expr') setMode('expr', { keep: true, silent: true });
            input.value = b.getAttribute('data-tt-sample');
            rebuild(true);
            ae('sample', { i: b.getAttribute('data-tt-sample-i') });
        });
    });
    document.querySelectorAll('[data-tt-mode]').forEach((b) => b.addEventListener('click', () => setMode(b.getAttribute('data-tt-mode'))));
    document.querySelectorAll('[data-tt-tab]').forEach((b) => b.addEventListener('click', () => setTab(b.getAttribute('data-tt-tab'), true)));
    // Стрелки по вкладкам — как у настоящего tablist.
    $('ttTabs').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const i = TABS.indexOf(state.tab);
        const dir = (e.key === 'ArrowRight') !== (document.documentElement.dir === 'rtl') ? 1 : -1;
        const next = TABS[(i + dir + TABS.length) % TABS.length];
        setTab(next, true);
        const btn = document.querySelector('[data-tt-tab="' + next + '"]');
        if (btn) btn.focus();
        e.preventDefault();
    });

    // Делегирование: заметки, фильтры, клетки.
    document.getElementById('ttApp').addEventListener('click', (e) => {
        const el = e.target.closest('button,[data-r]');
        if (!el) return;
        if (el.hasAttribute('data-tt-assoc')) { savePref('impAssoc', el.getAttribute('data-tt-assoc')); rebuild(true); ae('imp_assoc', { v: state.prefs.impAssoc }); return; }
        if (el.hasAttribute('data-tt-split')) { savePref('split', el.getAttribute('data-tt-split')); syncSettings(); rebuild(true); ae('split', { v: state.prefs.split }); return; }
        if (el.id === 'ttEgeReveal') { state.ege.hidden = false; renderEge(); ae('ege_reveal', {}); return; }
        if (el.hasAttribute('data-tt-filter')) {
            state.filter = el.getAttribute('data-tt-filter');
            state.shown = ROW_PAGE;
            if (state.tab !== 'table') setTab('table');
            else renderTable();
            ae('filter', { v: state.filter });
            return;
        }
        if (el.hasAttribute('data-tt-fn')) { state.active = +el.getAttribute('data-tt-fn'); state.cache = {}; renderVerdict(); renderFnPicker(); renderTab(); return; }
        if (el.hasAttribute('data-tt-set')) { toggleCell(+el.getAttribute('data-tt-set')); return; }
        if (el.hasAttribute('data-r') && el.closest('#ttKmap')) { toggleCell(+el.getAttribute('data-r')); return; }
        if (el.hasAttribute('data-tt-tr')) {
            const key = el.getAttribute('data-tt-tr');
            const tr = state.trainer;
            const cur = tr.answers.get(key);
            tr.answers.set(key, cur == null ? 0 : cur === 0 ? 1 : null);
            if (tr.answers.get(key) == null) tr.answers.delete(key);
            tr.checked = false;
            el.textContent = tr.answers.has(key) ? cellText(tr.answers.get(key)) : '·';
            el.parentElement.classList.remove('ok', 'bad');
            renderTrainerMsg();
            return;
        }
        if (el.hasAttribute('data-tt-group')) {
            const g = +el.getAttribute('data-tt-group');
            state.kmFocus = state.kmFocus === g ? null : g;
            renderKmap();
            return;
        }
        if (el.hasAttribute('data-tt-km')) { savePref('kmTarget', +el.getAttribute('data-tt-km')); state.kmFocus = null; renderKmap(); ae('kmap_target', { v: state.prefs.kmTarget }); return; }
        if (el.hasAttribute('data-tt-csrc')) { savePref('circSrc', el.getAttribute('data-tt-csrc')); renderCircuit(); return; }
        if (el.hasAttribute('data-tt-gate')) { savePref('gate', el.getAttribute('data-tt-gate')); renderCircuit(); ae('gate_style', { v: state.prefs.gate }); return; }
        if (el.hasAttribute('data-tt-eg')) {
            const [i, c] = el.getAttribute('data-tt-eg').split(':');
            const row = state.ege.rows[+i];
            if (c === 'f') row.f ^= 1;
            else { const v = row.cells[+c]; row.cells[+c] = v == null ? 0 : v === 0 ? 1 : null; }
            state.ege.sample = -1;
            state.byUser = true;
            renderEge();
            saveHash();
            const again = document.querySelector('[data-tt-eg="' + i + ':' + c + '"]');
            if (again) again.focus();
            return;
        }
        if (el.hasAttribute('data-tt-copyf')) {
            const k = el.getAttribute('data-tt-copyf');
            if (state.formText && state.formText[k]) copyOut(state.formText[k], t('copied'), 'export', { fmt: 'formula', what: k });
            return;
        }
        if (el.hasAttribute('data-tt-ege-sample')) { loadEgeSample(+el.getAttribute('data-tt-ege-sample')); ae('ege_sample', { i: +el.getAttribute('data-tt-ege-sample') }); return; }
        if (el.hasAttribute('data-tt-recent')) {
            const x = recent[+el.getAttribute('data-tt-recent')];
            if (x) { $('ttInput').value = x; rebuild(true); ae('history', { pick: 1 }); }
            return;
        }
        if (el.id === 'ttRecentClear') { recent = []; try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'recent', recent); } catch (_) { /* хранилище недоступно */ } drawRecent(); ae('history', { clear: 1 }); return; }
        if (el.id === 'ttWideHide') { savePref('inter', false); syncSettings(); renderTable(); wideHint(); ae('inter', { v: 0, from: 'wide_hint' }); return; }
        // Плитки пустого состояния: режим «по таблице» или задача о столбцах.
        if (el.hasAttribute('data-tt-go')) {
            const go = el.getAttribute('data-tt-go');
            // Плитка показывает свой вектор — его и открываем, а не то, что осталось в поле.
            if (go === 'vector' && el.hasAttribute('data-tt-vec')) {
                $('ttVecN').value = '3';
                $('ttVecNames').value = 'A, B, C';
                $('ttVector').value = el.getAttribute('data-tt-vec');
                syncVecCount();
            }
            setMode(go);
            ae('start_tile', { mode: go });
        }
    });
    // Клавиатура на клетках карты Карно: Enter / пробел.
    $('ttKmap').addEventListener('keydown', (e) => {
        const r = e.target.getAttribute && e.target.getAttribute('data-r');
        if (r != null && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleCell(+r); }
    });

    $('ttMore').addEventListener('click', () => { state.shown += ROW_PAGE; renderTable(); ae('rows_more', { shown: state.shown }); });
    $('ttInterBtn').addEventListener('click', () => { savePref('inter', !state.prefs.inter); syncSettings(); renderTable(); wideHint(); ae('inter', { v: state.prefs.inter ? 1 : 0 }); });
    // нажатие — явный выбор: дальше он перебивает умолчание страны и помнится
    $('ttOrderBtn').addEventListener('click', () => { savePref('order', curOrder() === 'asc' ? 'desc' : 'asc'); renderTable(); ae('order', { v: state.prefs.order }); });
    $('ttTrainerBtn').addEventListener('click', toggleTrainer);
    $('ttTrainerCheck').addEventListener('click', checkTrainer);
    $('ttCopyTable').addEventListener('click', copyTable);
    $('ttCsv').addEventListener('click', downloadCsv);
    $('ttMd').addEventListener('click', () => copyFormat('md'));
    $('ttTex').addEventListener('click', () => copyFormat('tex'));
    $('ttPng').addEventListener('click', () => { const s = tableSvg(); svgToPng(s.svg, s.width, s.height, fileName('png'), 'table'); });
    $('ttKmPng').addEventListener('click', () => kmapExport('png'));
    $('ttKmSvg').addEventListener('click', () => kmapExport('svg'));
    $('ttCircPng').addEventListener('click', () => circuitExport('png'));
    $('ttCircSvg').addEventListener('click', () => circuitExport('svg'));
    $('ttShare').addEventListener('click', share);
    $('ttPdf').addEventListener('click', savePdf);
    $('ttPrint').addEventListener('click', () => { ae('print', {}); window.print(); });
    $('ttRestore').addEventListener('click', () => {
        if (state.fromExpr == null) return;
        input.value = state.fromExpr;
        state.fromExpr = null;
        setMode('expr', { keep: true, silent: true });
        rebuild(true);
    });

    // Настройки.
    const sel = (id, key, after) => {
        const el = $(id);
        el.value = String(state.prefs[key]);
        el.addEventListener('change', () => {
            const v = typeof PREF_KEYS[key] === 'number' ? +el.value : el.value;
            savePref(key, v);
            after();
            ae('pref', { key, v });
        });
    };
    sel('ttVarOrder', 'varOrder', () => rebuild(false));
    sel('ttImpAssoc', 'impAssoc', () => rebuild(false));
    sel('ttSplit', 'split', () => rebuild(true));
    document.querySelectorAll('[data-tt-not]').forEach((b) => b.addEventListener('click', () => {
        savePref('notation', b.getAttribute('data-tt-not'));
        syncSettings();
        renderReading();
        renderTab();
        ae('notation', { v: state.prefs.notation });
    }));
    document.querySelectorAll('[data-tt-val]').forEach((b) => b.addEventListener('click', () => {
        savePref('values', b.getAttribute('data-tt-val'));
        syncSettings();
        renderVerdict();
        renderTab();
        ae('values', { v: state.prefs.values });
    }));

    $('ttEgeAdd').addEventListener('click', () => { ensureEgeRows(); state.ege.rows.push({ cells: new Array(state.ege.rows[0].cells.length).fill(null), f: state.ege.rows[0].f }); renderEge(); saveHash(); });
    $('ttEgeDel').addEventListener('click', () => { if (state.ege.rows.length > 1) state.ege.rows.pop(); renderEge(); saveHash(); });
    $('ttEgeClear').addEventListener('click', () => { state.ege.rows = []; ensureEgeRows(); state.ege.sample = -1; renderEge(); saveHash(); });
    $('ttEgeNew').addEventListener('click', () => { newEgeTask(); ae('ege_new', {}); });
}

function syncSettings() {
    const seg = (attr, val) => document.querySelectorAll('[' + attr + ']').forEach((b) => {
        const on = b.getAttribute(attr) === val;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    seg('data-tt-not', state.prefs.notation);
    seg('data-tt-val', state.prefs.values);
    $('ttVarOrder').value = state.prefs.varOrder;
    $('ttImpAssoc').value = state.prefs.impAssoc;
    $('ttSplit').value = state.prefs.split;
}

/* ── запуск ──────────────────────────────────────────────────────────────── */
function init() {
    if (!$('ttApp')) return;
    // Что страница решила по стране — видно снаружи (стенд, поддержка): экзамен,
    // чьим заданием названа задача о столбцах («none» — подана нейтрально).
    $('ttApp').setAttribute('data-tt-exam', NT.exam || 'none');
    loadPrefs();
    // Кнопки «F / T» на странице может не быть (у страны свои буквы значений): сохранённый
    // на ней выбор F / T читается как свои буквы — человек выбирал буквы, а не цифры.
    if (!document.querySelector('[data-tt-val="' + state.prefs.values + '"]')) state.prefs.values = state.prefs.values === 'tf' ? 'word' : '01';
    try {
        const saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'recent', []) : [];
        recent = Array.isArray(saved) ? saved.filter((x) => typeof x === 'string' && x).slice(0, RECENT_MAX) : [];
    } catch (_) { recent = []; }
    bind();
    syncSettings();
    syncVecCount();
    const fromHash = loadHash();
    if (!fromHash) {
        $('ttInput').value = CFG.sample || '';
        setMode('expr', { keep: true, silent: true });
    }
    if (state.mode !== 'ege') setTab(state.tab);
    rebuild(false);
    if (state.mode !== 'ege') setTab(state.tab);
    // Опора для стендов: состояние видно снаружи без чтения пикселей.
    window.__ttState = state;
    drawRecent();
    stage('ready');
    let raf = 0;
    window.addEventListener('resize', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => { if (state.model && state.tab === 'table') wideHint(); }); });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

// Имена, нужные только сторожам движка в странице.
export { opSymbol, docHtml, docText };
